import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  Divider,
  Grid,
} from '@mui/material';
import {
  AccountBalance,
  ArrowBackIos,
  ArrowForwardIos,
  CheckCircle,
  Tune,
  Delete,
  Refresh,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  CalendarMonth,
  Store,
  CreditCard,
  Inventory2,
  Visibility,
  BookOnline,
  CardMembership,
  PictureAsPdf,
  ReceiptLong,
} from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import StudioPayoutsTab from '../components/studio-payments/StudioPayoutsTab';

// ── Types ──────────────────────────────────────────────────────────────────
interface StudioRef {
  _id: string;
  studioName: string;
  email: string;
}

interface WeeklyStatement {
  _id: string;
  studio: StudioRef;
  weekStart: string;
  weekEnd: string;
  invoiceNumber: string;
  totalRevenue: number;
  baseRevenue: number;
  gst: number;
  bookingRevenue: number;
  packRevenue: number;
  bookingCount: number;
  packCount: number;
  razorpayFee: number;
  netSettlement: number;
  discountAmount?: number;
  discountLabel?: string;
  // Manual correction for money the automatic calculation can't see (e.g. a
  // captured pack payment orphaned when its purchased pack was deleted).
  // Signed: positive pays the studio more. Applied after the gateway fee.
  adjustmentAmount?: number;
  // Refunds SweatSocial paid back for an already-settled week. Subtracted from
  // every payable figure below — leaving it out of any one of them shows an
  // amount the studio will not actually receive.
  refundDeduction?: number;
  refundDeductionNote?: string;
  adjustmentLabel?: string;
  adjustmentNote?: string;
  adjustmentBy?: string;
  adjustmentAt?: string;
  // net + adjustment − discount, computed server-side. What actually transfers.
  finalSettlement?: number;
  status: 'pending' | 'paid';
  isOverdue: boolean;
  isPartialWeek: boolean;
  paidAt?: string;
  paidBy?: string;
  transactionRef?: string;
  notes?: string;
  generatedAt: string;
}

interface MRRPeriod {
  total: number;
  baseRevenue: number;
  gst: number;
  bookingRevenue: number;
  packRevenue: number;
  studioCount: number;
  bookingCount: number;
  packCount: number;
}

interface MRRData {
  currentMonth: MRRPeriod;
  lastMonth: MRRPeriod;
  growthPercent: number | null;
}

interface BookingDetail {
  bookingId: string;
  user: { name: string; email: string; phone: string } | null;
  label: string;
  sessionDate: string | null;
  bookingStatus: string;
  basePrice: number;
  taxes: number;
  total: number;
  modeOfPayment: string;
  /** Cash / pay-at-studio — money already with the studio; listed but excluded from totals */
  collectedAtStudio?: boolean;
}

interface PackDetail {
  user: { name: string; email: string; phone: string } | null;
  packName: string;
  totalClasses: number | null;
  purchasedAt: string;
  basePrice: number;
  taxes: number;
  total: number;
  modeOfPayment: string;
  /** Cash / pay-at-studio — money already with the studio; listed but excluded from totals */
  collectedAtStudio?: boolean;
}

interface StatementDetails {
  bookings: BookingDetail[];
  packs: PackDetail[];
  totals: { bookingRevenue: number; packRevenue: number; total: number; bookingCount: number; packCount: number };
}

// ── Helpers ────────────────────────────────────────────────────────────────
const formatINR = (amount: number) =>
  `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};


// Returns a UTC-midnight Date for the most-recent Friday in IST
function getLatestFriday(): Date {
  // Get today's date string in IST (e.g. "2026-05-23")
  const istStr = new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [y, m, d] = istStr.split('-').map(Number);
  // Build a UTC-midnight date so getUTCDay() equals the IST day-of-week
  const todayUTC = new Date(Date.UTC(y, m - 1, d));
  const dow = todayUTC.getUTCDay(); // 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
  // days back to reach most-recent Friday (dow 5)
  const daysBack = dow >= 5 ? dow - 5 : dow + 2;
  return new Date(Date.UTC(y, m - 1, d - daysBack));
}

function toDateStr(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Always produce a clean UTC-midnight date to avoid IST-day cross-midnight drift
function addDays(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
}

function formatWeekLabel(friDate: Date): string {
  const thu = addDays(friDate, 6);
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };
  const friStr = friDate.toLocaleDateString('en-IN', { ...opts, timeZone: 'Asia/Kolkata' });
  const thuStr = thu.toLocaleDateString('en-IN', { ...opts, year: 'numeric', timeZone: 'Asia/Kolkata' });
  return `${friStr} – ${thuStr}`;
}

// ── Stat Card ──────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  iconColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, icon, iconColor, loading }) => (
  <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {label}
          </Typography>
          {loading ? (
            <>
              <Skeleton width="60%" height={36} />
              <Skeleton width="40%" height={20} />
            </>
          ) : (
            <>
              <Typography variant="h4" fontWeight={700}>
                {value}
              </Typography>
              {sub && (
                <Typography variant="caption" color="text.secondary">
                  {sub}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Box sx={{ color: iconColor, opacity: 0.9 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Status Chip ────────────────────────────────────────────────────────────
const StatusChip: React.FC<{ status: string; isOverdue?: boolean }> = ({ status, isOverdue }) => {
  if (status === 'paid') return <Chip label="Paid" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, borderRadius: 1.5 }} />;
  if (isOverdue) return <Chip label="Due" size="small" sx={{ bgcolor: '#fbe9e7', color: '#bf360c', fontWeight: 600, borderRadius: 1.5 }} />;
  return <Chip label="Pending" size="small" sx={{ bgcolor: '#fff8e1', color: '#e65100', fontWeight: 600, borderRadius: 1.5 }} />;
};

// ── Main Page ──────────────────────────────────────────────────────────────
const StudioPaymentsPage: React.FC = () => {
  const [mrrData, setMrrData] = useState<MRRData | null>(null);
  const [mrrLoading, setMrrLoading] = useState(true);

  const [pageTab, setPageTab] = useState(0); // 0=Revenue Statements 1=Payouts
  const [payoutsMounted, setPayoutsMounted] = useState(false);

  const [weekDate, setWeekDate] = useState<Date>(() => getLatestFriday());
  const [tab, setTab] = useState(0); // 0=All 1=Pending/Overdue 2=Paid

  const [statements, setStatements] = useState<WeeklyStatement[]>([]);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtError, setStmtError] = useState<string | null>(null);

  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<{ created: number; updated: number; skippedPaid: number } | null>(null);

  // Mark as Paid dialog
  const [markPaidStmt, setMarkPaidStmt] = useState<WeeklyStatement | null>(null);
  const [txRef, setTxRef] = useState('');
  const [txNotes, setTxNotes] = useState('');
  const [discountAmt, setDiscountAmt] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);

  // Delete dialog
  // Settlement adjustment dialog
  const [adjustStmt, setAdjustStmt] = useState<WeeklyStatement | null>(null);
  const [adjustAmt, setAdjustAmt] = useState('');
  const [adjustLabel, setAdjustLabel] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [savingAdjust, setSavingAdjust] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const [deleteStmt, setDeleteStmt] = useState<WeeklyStatement | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Details dialog
  const [detailsStmt, setDetailsStmt] = useState<WeeklyStatement | null>(null);
  const [detailsData, setDetailsData] = useState<StatementDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);

  // Invoice preview / send
  const [invoiceLoading, setInvoiceLoading] = useState<string | null>(null); // statement _id being loaded
  const [sendInvoiceChecked, setSendInvoiceChecked] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const weekStartStr = toDateStr(weekDate);

  const fetchStatements = useCallback(async () => {
    setStmtLoading(true);
    setStmtError(null);
    setGenerateResult(null);
    try {
      const params: Record<string, any> = { weekStart: weekStartStr };
      if (tab === 2) params.status = 'paid';
      // tab 0 = all, tab 1 = show pending (both overdue and non-overdue)
      else if (tab === 1) params.status = 'pending';
      const res = await apiService.getStudioPaymentStatements(params);
      const data = res?.data ?? res;
      setStatements(data?.statements ?? []);
    } catch (e: any) {
      setStmtError(e?.response?.data?.message ?? 'Failed to load statements');
    } finally {
      setStmtLoading(false);
    }
  }, [weekStartStr, tab]);

  const fetchMRR = useCallback(async () => {
    setMrrLoading(true);
    try {
      const res = await apiService.getStudioPaymentsMRR();
      const data = res?.data ?? res;
      setMrrData(data);
    } catch {
      // non-critical
    } finally {
      setMrrLoading(false);
    }
  }, []);

  useEffect(() => { fetchMRR(); }, [fetchMRR]);
  useEffect(() => {
    if (pageTab === 0) fetchStatements();
  }, [fetchStatements, pageTab]);
  useEffect(() => {
    if (pageTab === 1) setPayoutsMounted(true);
  }, [pageTab]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateResult(null);
    setStmtError(null);
    try {
      const res = await apiService.generateStudioPaymentStatements(weekStartStr);
      const data = res?.data ?? res;
      setGenerateResult({ created: data.created, updated: data.updated, skippedPaid: data.skippedPaid });
      await fetchStatements();
      await fetchMRR();
    } catch (e: any) {
      setStmtError(e?.response?.data?.message ?? 'Failed to generate statements');
    } finally {
      setGenerating(false);
    }
  };

  const handleAdjustOpen = (stmt: WeeklyStatement) => {
    setAdjustStmt(stmt);
    setAdjustAmt(stmt.adjustmentAmount ? String(stmt.adjustmentAmount) : '');
    setAdjustLabel(stmt.adjustmentLabel ?? '');
    setAdjustNote(stmt.adjustmentNote ?? '');
    setAdjustError(null);
  };

  const handleAdjustSave = async () => {
    if (!adjustStmt) return;
    const amount = adjustAmt.trim() === '' ? 0 : Number(adjustAmt);
    if (!Number.isFinite(amount)) {
      setAdjustError('Enter a valid amount.');
      return;
    }
    // Mirror the server guard so the operator sees the problem before saving.
    const projected =
      (adjustStmt.netSettlement ?? 0) +
      amount -
      (adjustStmt.discountAmount ?? 0) -
      (adjustStmt.refundDeduction ?? 0);
    if (projected < 0) {
      setAdjustError(
        `That would make the payable amount ${formatINR(projected)}. It can't go below zero.`,
      );
      return;
    }
    if (amount !== 0 && !adjustLabel.trim()) {
      setAdjustError('Add a short label — it appears on the studio\'s invoice.');
      return;
    }

    setSavingAdjust(true);
    setAdjustError(null);
    try {
      await apiService.setStudioPaymentAdjustment(
        adjustStmt._id,
        amount,
        adjustLabel.trim() || undefined,
        adjustNote.trim() || undefined,
      );
      setAdjustStmt(null);
      await fetchStatements();
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message ?? 'Failed to save the adjustment');
    } finally {
      setSavingAdjust(false);
    }
  };

  const handleMarkPaidOpen = (stmt: WeeklyStatement) => {
    setMarkPaidStmt(stmt);
    setTxRef('');
    setTxNotes('');
    setDiscountAmt('');
    setDiscountLabel('');
    setMarkPaidError(null);
    setSendInvoiceChecked(false);
  };

  const handleDetailsOpen = async (stmt: WeeklyStatement) => {
    setDetailsStmt(stmt);
    setDetailsData(null);
    setDetailsTab(0);
    setDetailsLoading(true);
    try {
      const res = await apiService.getStudioPaymentDetails(stmt._id);
      setDetailsData(res?.data ?? res);
    } catch {
      setDetailsData({ bookings: [], packs: [], totals: { bookingRevenue: 0, packRevenue: 0, total: 0, bookingCount: 0, packCount: 0 } });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewInvoice = async (stmt: WeeklyStatement) => {
    setInvoiceLoading(stmt._id);
    try {
      // Pass the statement's EXACT week boundaries (full ISO instants).
      // Truncating to UTC date strings (toDateStr) shifted the window — the
      // IST week start landed a day early and the end was re-extended server
      // side, so the boundary day's transactions showed up on BOTH this
      // week's and the previous week's invoice.
      const blob = await apiService.generateStudioInvoice(stmt.studio._id, {
        startDate: new Date(stmt.weekStart).toISOString(),
        endDate: new Date(stmt.weekEnd).toISOString(),
        // Include a saved discount so the invoice matches what was settled.
        discountAmount: (stmt.discountAmount ?? 0) > 0 ? stmt.discountAmount : undefined,
        discountLabel: (stmt.discountAmount ?? 0) > 0 ? stmt.discountLabel : undefined,
        // …and the saved adjustment, for the same reason. Without this the PDF
        // silently shows the pre-adjustment net while the row shows the real
        // payable amount — the two disagreeing is exactly what this feature is
        // meant to prevent.
        adjustmentAmount: (stmt.adjustmentAmount ?? 0) !== 0 ? stmt.adjustmentAmount : undefined,
        adjustmentLabel: (stmt.adjustmentAmount ?? 0) !== 0 ? stmt.adjustmentLabel : undefined,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      // silently fail — user will see nothing opened
    } finally {
      setInvoiceLoading(null);
    }
  };

  // Preview the receipt PDF with the discount currently typed (not yet saved),
  // so the admin can see exactly how it will look before confirming/sending.
  const handlePreviewInvoice = async () => {
    if (!markPaidStmt) return;
    const discount = discountAmt.trim() ? Number(discountAmt.trim()) : 0;
    if (discountAmt.trim() && (Number.isNaN(discount) || discount < 0)) {
      setMarkPaidError('Enter a valid discount amount.');
      return;
    }
    setPreviewLoading(true);
    setMarkPaidError(null);
    try {
      const blob = await apiService.generateStudioInvoice(markPaidStmt.studio._id, {
        startDate: new Date(markPaidStmt.weekStart).toISOString(),
        endDate: new Date(markPaidStmt.weekEnd).toISOString(),
        discountAmount: discount > 0 ? discount : undefined,
        discountLabel: discount > 0 ? discountLabel.trim() || undefined : undefined,
        // Carry the saved adjustment through so the preview matches the invoice
        // the studio will actually receive.
        adjustmentAmount: markPaidStmt.adjustmentAmount || undefined,
        adjustmentLabel: markPaidStmt.adjustmentLabel || undefined,
      });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      setMarkPaidError('Could not load the preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMarkPaidConfirm = async () => {
    if (!markPaidStmt || !txRef.trim()) return;
    // Optional settlement discount granted by the studio — validated client-side
    // (backend re-validates); must be within [0, net settlement].
    const discount = discountAmt.trim() ? Number(discountAmt.trim()) : 0;
    if (discountAmt.trim() && (Number.isNaN(discount) || discount < 0)) {
      setMarkPaidError('Enter a valid discount amount.');
      return;
    }
    // Cap against what is actually payable — net, plus any adjustment, minus
    // any refund clawback. Must match finalSettlement() on the server, or the
    // panel accepts a discount the API then rejects.
    const discountCeiling =
      (markPaidStmt.netSettlement ?? 0) +
      (markPaidStmt.adjustmentAmount ?? 0) -
      (markPaidStmt.refundDeduction ?? 0);
    if (discount > discountCeiling) {
      setMarkPaidError(
        `Discount can't exceed the payable amount (${formatINR(discountCeiling)}).`,
      );
      return;
    }
    setMarkingPaid(true);
    setMarkPaidError(null);
    try {
      await apiService.markStudioPaymentPaid(
        markPaidStmt._id,
        txRef.trim(),
        txNotes.trim() || undefined,
        discount > 0 ? discount : undefined,
        discount > 0 ? discountLabel.trim() || undefined : undefined,
      );
      const stmtId = markPaidStmt._id;
      setMarkPaidStmt(null);
      await fetchStatements();
      await fetchMRR();
      if (sendInvoiceChecked) {
        setSendingInvoice(true);
        try {
          await apiService.sendStudioPaymentInvoice(stmtId);
        } catch {
          // non-critical
        } finally {
          setSendingInvoice(false);
        }
      }
      setSendInvoiceChecked(false);
    } catch (e: any) {
      setMarkPaidError(e?.response?.data?.message ?? 'Failed to mark as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteStmt) return;
    setDeleting(true);
    try {
      await apiService.deleteStudioPaymentStatement(deleteStmt._id);
      setDeleteStmt(null);
      await fetchStatements();
    } catch (e: any) {
      setStmtError(e?.response?.data?.message ?? 'Failed to delete statement');
      setDeleteStmt(null);
    } finally {
      setDeleting(false);
    }
  };

  const growthPercent = mrrData?.growthPercent ?? null;
  const GrowthIcon =
    growthPercent === null ? TrendingFlat
    : growthPercent > 0 ? TrendingUp
    : growthPercent < 0 ? TrendingDown
    : TrendingFlat;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AccountBalance sx={{ color: colors.primary, fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Studio Payments</Typography>
          <Typography variant="body2" color="text.secondary">
            Revenue statements and weekly payouts for all studios
          </Typography>
        </Box>
      </Box>

      {/* MRR Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="This Month Revenue"
            value={mrrLoading ? '' : formatINR(mrrData?.currentMonth.total ?? 0)}
            sub={mrrLoading ? '' : `${mrrData?.currentMonth.studioCount ?? 0} studios`}
            icon={<AccountBalance sx={{ fontSize: 40 }} />}
            iconColor={colors.info}
            loading={mrrLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Last Month Revenue"
            value={mrrLoading ? '' : formatINR(mrrData?.lastMonth.total ?? 0)}
            sub={mrrLoading ? '' : `${mrrData?.lastMonth.studioCount ?? 0} studios`}
            icon={<CalendarMonth sx={{ fontSize: 40 }} />}
            iconColor="#6a1b9a"
            loading={mrrLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Month-over-Month Growth"
            value={mrrLoading ? '' : growthPercent === null ? 'N/A' : `${growthPercent > 0 ? '+' : ''}${growthPercent}%`}
            sub={mrrLoading ? '' : 'vs last month'}
            icon={<GrowthIcon sx={{ fontSize: 40 }} />}
            iconColor={growthPercent !== null && growthPercent > 0 ? colors.success : growthPercent !== null && growthPercent < 0 ? colors.error : colors.textSecondary}
            loading={mrrLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="This Month Bookings + Packs"
            value={mrrLoading ? '' : `${(mrrData?.currentMonth.bookingCount ?? 0) + (mrrData?.currentMonth.packCount ?? 0)}`}
            sub={mrrLoading ? '' : `${mrrData?.currentMonth.bookingCount ?? 0} bookings · ${mrrData?.currentMonth.packCount ?? 0} packs`}
            icon={<Inventory2 sx={{ fontSize: 40 }} />}
            iconColor={colors.warning}
            loading={mrrLoading}
          />
        </Grid>
      </Grid>

      {/* Page tab selector — card-style tiles */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {([
          { index: 0, title: 'Revenue Statements', subtitle: 'Weekly invoices per studio', icon: <ReceiptLong sx={{ fontSize: 28 }} /> },
          { index: 1, title: 'Payouts', subtitle: 'Studio settlement history', icon: <AccountBalance sx={{ fontSize: 28 }} /> },
        ] as const).map((pageTile) => {
          const active = pageTab === pageTile.index;
          return (
            <Grid key={pageTile.index} size={{ xs: 12, sm: 6 }}>
              <Card
                onClick={() => setPageTab(pageTile.index)}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: active ? colors.primary : colors.divider,
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: active ? colors.primary : `${colors.primary}60` },
                }}
              >
                <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: active ? `${colors.primary}15` : colors.backgroundTertiary,
                      color: active ? colors.primary : 'text.secondary',
                      display: 'flex',
                    }}
                  >
                    {pageTile.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={700} color={active ? 'text.primary' : 'text.secondary'}>
                      {pageTile.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pageTile.subtitle}
                    </Typography>
                  </Box>
                  {active && (
                    <CheckCircle sx={{ color: colors.primary, fontSize: 22, flexShrink: 0 }} />
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {pageTab === 0 && (
      <>
      {/* Week Picker + Generate */}
      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Previous week">
                <IconButton
                  size="small"
                  onClick={() => setWeekDate(prev => addDays(prev, -7))}
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <ArrowBackIos sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>

              <Box sx={{
                px: 2.5, py: 1, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <CalendarMonth sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600}>
                  {formatWeekLabel(weekDate)}
                </Typography>
                {weekDate.getTime() >= getLatestFriday().getTime() && (
                  <Chip label="Current" size="small" sx={{ ml: 0.5, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, height: 20, fontSize: 11 }} />
                )}
              </Box>

              <Tooltip title="Next week">
                <IconButton
                  size="small"
                  onClick={() => setWeekDate(prev => addDays(prev, 7))}
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <ArrowForwardIos sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Tooltip title="Jump to current week">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setWeekDate(getLatestFriday())}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                This Week
              </Button>
            </Tooltip>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={handleGenerate}
              disabled={generating}
              sx={{
                textTransform: 'none', borderRadius: 2,
                bgcolor: colors.primary, fontWeight: 600,
                '&:hover': { bgcolor: colors.primary, filter: 'brightness(0.92)' },
              }}
            >
              {generating ? 'Generating…' : 'Generate Statements'}
            </Button>
          </Box>

          {sendingInvoice && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              Sending invoice email… <CircularProgress size={12} sx={{ ml: 1 }} />
            </Alert>
          )}

          {generateResult && (
            <Alert
              severity="success"
              sx={{ mt: 2, borderRadius: 2 }}
              onClose={() => setGenerateResult(null)}
            >
              Generated — <strong>{generateResult.created} new</strong>, <strong>{generateResult.updated} updated</strong>
              {generateResult.skippedPaid > 0 && `, ${generateResult.skippedPaid} paid (skipped)`}.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Statements Table */}
      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}
          >
            <Tab label="All Statements" />
            <Tab label="Pending / Overdue" />
            <Tab label="Paid" />
          </Tabs>
        </Box>

        {stmtError && (
          <Alert severity="error" sx={{ m: 2, borderRadius: 2 }} onClose={() => setStmtError(null)}>
            {stmtError}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa', fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                <TableCell>Studio</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Bookings</TableCell>
                <TableCell align="right">Packs</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Rzp Fee</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stmtLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}><Skeleton height={20} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : statements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Store sx={{ fontSize: 48, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography color="text.secondary" variant="body2">
                      No statements for this week.
                    </Typography>
                    <Typography color="text.disabled" variant="caption">
                      Click "Generate Statements" to compute revenue for this period.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                statements.map((stmt) => (
                  <TableRow
                    key={stmt._id}
                    hover
                    sx={{
                      '&:last-child td': { border: 0 },
                      ...(stmt.isOverdue && stmt.status === 'pending' && { bgcolor: '#fff8f6' }),
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160 }}>
                        {stmt.studio?.studioName ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                        {stmt.studio?.email ?? ''}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {stmt.invoiceNumber}
                      </Typography>
                      {stmt.isPartialWeek && (
                        <Chip label="Partial" size="small" sx={{ ml: 0.5, height: 16, fontSize: 10, bgcolor: '#fff8e1', color: '#e65100' }} />
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatDate(stmt.weekStart)} –
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {formatDate(stmt.weekEnd)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">{formatINR(stmt.bookingRevenue)}</Typography>
                      <Typography variant="caption" color="text.secondary">{stmt.bookingCount} bkg</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">{formatINR(stmt.packRevenue)}</Typography>
                      <Typography variant="caption" color="text.secondary">{stmt.packCount} pack</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700}>
                        {formatINR(stmt.totalRevenue)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                        Base {formatINR(stmt.baseRevenue)} + GST {formatINR(stmt.gst)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">{formatINR(stmt.razorpayFee ?? 0)}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="#2e7d32">
                        {formatINR(stmt.netSettlement ?? 0)}
                      </Typography>
                      {(stmt.discountAmount ?? 0) > 0 && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          −{formatINR(stmt.discountAmount ?? 0)} discount
                        </Typography>
                      )}
                      {(stmt.adjustmentAmount ?? 0) !== 0 && (
                        <Tooltip title={stmt.adjustmentNote || stmt.adjustmentLabel || 'Settlement adjustment'}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ display: 'block', color: (stmt.adjustmentAmount ?? 0) > 0 ? '#2e7d32' : '#c62828' }}
                          >
                            {(stmt.adjustmentAmount ?? 0) > 0 ? '+' : '−'}
                            {formatINR(Math.abs(stmt.adjustmentAmount ?? 0))} adjustment
                          </Typography>
                        </Tooltip>
                      )}
                      {(stmt.refundDeduction ?? 0) > 0 && (
                        <Tooltip title={stmt.refundDeductionNote || 'Refunds paid back for a week already settled'}>
                          <Typography variant="caption" noWrap sx={{ display: 'block', color: '#c62828' }}>
                            −{formatINR(stmt.refundDeduction ?? 0)} refunds
                          </Typography>
                        </Tooltip>
                      )}
                      {((stmt.discountAmount ?? 0) > 0 || (stmt.adjustmentAmount ?? 0) !== 0 || (stmt.refundDeduction ?? 0) > 0) && (
                        <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', color: '#1565c0' }}>
                          → {formatINR(stmt.finalSettlement ?? (stmt.netSettlement ?? 0))}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <StatusChip status={stmt.status} isOverdue={stmt.isOverdue} />
                      {stmt.status === 'paid' && stmt.paidAt && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                          {formatDate(stmt.paidAt)}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="View booking details">
                          <IconButton
                            size="small"
                            onClick={() => handleDetailsOpen(stmt)}
                            sx={{ color: '#1565c0', '&:hover': { bgcolor: '#e3f2fd' } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Invoice PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleViewInvoice(stmt)}
                            disabled={invoiceLoading === stmt._id}
                            sx={{ color: '#6a1b9a', '&:hover': { bgcolor: '#f3e5f5' } }}
                          >
                            {invoiceLoading === stmt._id
                              ? <CircularProgress size={14} />
                              : <PictureAsPdf fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {stmt.status === 'pending' && (
                          <Tooltip title="Add / edit settlement adjustment">
                            <IconButton
                              size="small"
                              onClick={() => handleAdjustOpen(stmt)}
                              sx={{ color: '#ef6c00', '&:hover': { bgcolor: '#fff3e0' } }}
                            >
                              <Tune fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {stmt.status === 'pending' && (
                          <Tooltip title="Mark as Paid">
                            <IconButton
                              size="small"
                              onClick={() => handleMarkPaidOpen(stmt)}
                              sx={{ color: '#2e7d32', '&:hover': { bgcolor: '#e8f5e9' } }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {stmt.status === 'pending' && (
                          <Tooltip title="Delete Statement">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteStmt(stmt)}
                              sx={{ color: '#c62828', '&:hover': { bgcolor: '#ffebee' } }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {stmt.status === 'paid' && (
                          <Tooltip title={`Paid via ${stmt.transactionRef ?? 'N/A'}${stmt.paidBy ? ` by ${stmt.paidBy}` : ''}`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CreditCard sx={{ fontSize: 16, color: '#2e7d32' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 90 }} noWrap>
                                {stmt.transactionRef ?? '—'}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!stmtLoading && statements.length > 0 && (
          <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 3 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>{statements.length}</strong> studios
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: <strong>{formatINR(statements.reduce((s, r) => s + r.totalRevenue, 0))}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pending: <strong>{formatINR(statements.filter(r => r.status === 'pending').reduce((s, r) => s + r.totalRevenue, 0))}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Paid: <strong>{formatINR(statements.filter(r => r.status === 'paid').reduce((s, r) => s + r.totalRevenue, 0))}</strong>
            </Typography>
          </Box>
        )}
      </Card>

      {/* ── Mark as Paid Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={!!markPaidStmt}
        onClose={() => !markingPaid && setMarkPaidStmt(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircle sx={{ color: '#2e7d32' }} />
            <Box>
              <Typography fontWeight={700}>Mark as Paid</Typography>
              <Typography variant="caption" color="text.secondary">
                {markPaidStmt?.studio?.studioName} · {markPaidStmt?.invoiceNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {markPaidStmt && (
            <Box sx={{ bgcolor: '#f9fbe7', borderRadius: 2, p: 2, mb: 2.5 }}>
              <Typography variant="body2" fontWeight={600}>
                Amount: {formatINR(markPaidStmt.totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(markPaidStmt.weekStart)} – {formatDate(markPaidStmt.weekEnd)} ·
                Base {formatINR(markPaidStmt.baseRevenue)} + GST {formatINR(markPaidStmt.gst)}
              </Typography>
            </Box>
          )}

          {markPaidError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{markPaidError}</Alert>
          )}

          <TextField
            label="Transaction Reference *"
            fullWidth
            value={txRef}
            onChange={(e) => setTxRef(e.target.value)}
            placeholder="e.g. UTR123456789 or NEFT/IMPS ref"
            sx={{ mb: 2 }}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={2}
            value={txNotes}
            onChange={(e) => setTxNotes(e.target.value)}
            placeholder="Any additional payment notes"
            InputProps={{ sx: { borderRadius: 2 } }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <TextField
              label="Discount (optional)"
              type="number"
              value={discountAmt}
              onChange={(e) => setDiscountAmt(e.target.value)}
              placeholder="0"
              helperText="Studio's concession — lowers the payout"
              InputProps={{
                sx: { borderRadius: 2 },
                startAdornment: <span style={{ marginRight: 4 }}>₹</span>,
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Discount label (optional)"
              value={discountLabel}
              onChange={(e) => setDiscountLabel(e.target.value)}
              placeholder="e.g. Studio Discount"
              helperText="Shown on the receipt"
              InputProps={{ sx: { borderRadius: 2 } }}
              sx={{ flex: 1 }}
            />
          </Box>
          {markPaidStmt && ((Number(discountAmt) || 0) > 0 || (markPaidStmt.adjustmentAmount ?? 0) !== 0 || (markPaidStmt.refundDeduction ?? 0) > 0) && (
            <Box sx={{ bgcolor: '#f1f8e9', borderRadius: 2, p: 1.5, mb: 2 }}>
              <Typography variant="body2">
                Net {formatINR(markPaidStmt.netSettlement ?? 0)}
                {(Number(discountAmt) || 0) > 0 && ` − ${formatINR(Number(discountAmt) || 0)}`}
                {(markPaidStmt.adjustmentAmount ?? 0) !== 0 &&
                  ` ${(markPaidStmt.adjustmentAmount ?? 0) > 0 ? '+' : '−'} ${formatINR(Math.abs(markPaidStmt.adjustmentAmount ?? 0))}`}
                {(markPaidStmt.refundDeduction ?? 0) > 0 &&
                  ` − ${formatINR(markPaidStmt.refundDeduction ?? 0)} refunds`}
                {' = '}
                <strong>
                  {formatINR(
                    (markPaidStmt.netSettlement ?? 0)
                    - (Number(discountAmt) || 0)
                    + (markPaidStmt.adjustmentAmount ?? 0)
                    - (markPaidStmt.refundDeduction ?? 0),
                  )}
                </strong>{' '}
                payable
              </Typography>
              {(markPaidStmt.adjustmentAmount ?? 0) !== 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Adjustment: {markPaidStmt.adjustmentLabel}
                  {markPaidStmt.adjustmentBy ? ` · set by ${markPaidStmt.adjustmentBy}` : ''}
                </Typography>
              )}
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={sendInvoiceChecked}
                onChange={(e) => setSendInvoiceChecked(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Send invoice to{' '}
                <strong>{markPaidStmt?.studio?.email ?? 'studio email'}</strong>
              </Typography>
            }
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handlePreviewInvoice}
            disabled={previewLoading || markingPaid}
            startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
            sx={{ textTransform: 'none', borderRadius: 2, mr: 'auto' }}
          >
            {previewLoading ? 'Loading…' : 'Preview receipt'}
          </Button>
          <Button onClick={() => setMarkPaidStmt(null)} disabled={markingPaid} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMarkPaidConfirm}
            disabled={markingPaid || !txRef.trim()}
            startIcon={markingPaid ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {markingPaid ? 'Saving…' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Settlement Adjustment Dialog ────────────────────────────────────── */}
      <Dialog
        open={!!adjustStmt}
        onClose={() => !savingAdjust && setAdjustStmt(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tune sx={{ color: '#ef6c00' }} />
            <Box>
              <Typography fontWeight={700}>Settlement Adjustment</Typography>
              <Typography variant="caption" color="text.secondary">
                {adjustStmt?.studio?.studioName} · {adjustStmt?.invoiceNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            For money owed to this studio that the weekly calculation can't see —
            e.g. a captured pack payment whose purchased pack was deleted.
            Enter the <strong>net</strong> figure from Razorpay (gross minus the
            actual fee); the 2.5% gateway fee is <strong>not</strong> applied
            again. Use a negative amount to claw back.
          </Alert>

          {adjustError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{adjustError}</Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Adjustment amount (₹)"
            type="number"
            value={adjustAmt}
            onChange={(e) => setAdjustAmt(e.target.value)}
            placeholder="0"
            helperText="Positive pays the studio more · negative claws back · 0 clears it"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Label (shown on the studio's invoice)"
            value={adjustLabel}
            onChange={(e) => setAdjustLabel(e.target.value)}
            placeholder="e.g. Recovered pack payments — 20 Jul"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Internal note (optional)"
            value={adjustNote}
            onChange={(e) => setAdjustNote(e.target.value)}
            placeholder="Payment ids, why it was needed, who authorised it"
            multiline
            minRows={2}
          />

          {adjustStmt && (
            <Box sx={{ mt: 2.5, p: 1.75, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Net {formatINR(adjustStmt.netSettlement ?? 0)}
                {(adjustStmt.discountAmount ?? 0) > 0 && ` − ${formatINR(adjustStmt.discountAmount ?? 0)} discount`}
                {adjustAmt.trim() !== '' && Number.isFinite(Number(adjustAmt)) && Number(adjustAmt) !== 0 &&
                  ` ${Number(adjustAmt) > 0 ? '+' : '−'} ${formatINR(Math.abs(Number(adjustAmt)))} adjustment`}
                {(adjustStmt.refundDeduction ?? 0) > 0 &&
                  ` − ${formatINR(adjustStmt.refundDeduction ?? 0)} refunds`}
              </Typography>
              <Typography variant="h6" fontWeight={700} color="#2e7d32">
                Studio will be paid{' '}
                {formatINR(
                  (adjustStmt.netSettlement ?? 0)
                  - (adjustStmt.discountAmount ?? 0)
                  + (adjustAmt.trim() === '' || !Number.isFinite(Number(adjustAmt)) ? 0 : Number(adjustAmt))
                  - (adjustStmt.refundDeduction ?? 0),
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setAdjustStmt(null)}
            disabled={savingAdjust}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdjustSave}
            disabled={savingAdjust}
            startIcon={savingAdjust ? <CircularProgress size={16} color="inherit" /> : <Tune />}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}
          >
            {savingAdjust ? 'Saving…' : 'Save Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={!!deleteStmt}
        onClose={() => !deleting && setDeleteStmt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Delete sx={{ color: '#c62828' }} />
            <Typography fontWeight={700}>Delete Statement</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2">
            Are you sure you want to delete the statement for{' '}
            <strong>{deleteStmt?.studio?.studioName}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Invoice: {deleteStmt?.invoiceNumber} · {formatINR(deleteStmt?.totalRevenue ?? 0)} — This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteStmt(null)} disabled={deleting} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Statement Details Dialog ────────────────────────────────────────── */}
      <Dialog
        open={!!detailsStmt}
        onClose={() => setDetailsStmt(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Visibility sx={{ color: '#1565c0' }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700}>{detailsStmt?.studio?.studioName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {detailsStmt?.invoiceNumber} · {detailsStmt ? `${formatDate(detailsStmt.weekStart)} – ${formatDate(detailsStmt.weekEnd)}` : ''}
              </Typography>
            </Box>
            {detailsData && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" fontWeight={700}>{formatINR(detailsData.totals.total)}</Typography>
                <Typography variant="caption" color="text.secondary">total revenue</Typography>
              </Box>
            )}
          </Box>
          <Tabs
            value={detailsTab}
            onChange={(_, v) => setDetailsTab(v)}
            sx={{ mt: 1.5, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: 13 } }}
          >
            <Tab
              icon={<BookOnline sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={detailsData ? `Bookings (${detailsData.bookings.length}) · ${formatINR(detailsData.totals.bookingRevenue)}` : 'Bookings'}
            />
            <Tab
              icon={<CardMembership sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={detailsData ? `Packs (${detailsData.packs.length}) · ${formatINR(detailsData.totals.packRevenue)}` : 'Packs'}
            />
          </Tabs>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {detailsLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>Loading details…</Typography>
            </Box>
          ) : detailsData && detailsTab === 0 ? (
            /* ── Bookings Tab ── */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa', fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Session / Event</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsData.bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No online bookings this week</Typography>
                      </TableCell>
                    </TableRow>
                  ) : detailsData.bookings.map((b, i) => (
                    <TableRow key={b.bookingId?.toString() ?? i} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.disabled', fontSize: 12 }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>{b.user?.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140, display: 'block' }}>{b.user?.phone || b.user?.email || ''}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{b.label || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {b.sessionDate ? formatDate(b.sessionDate) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={b.bookingStatus}
                          size="small"
                          sx={{
                            height: 20, fontSize: 10, fontWeight: 600, borderRadius: 1,
                            bgcolor: b.bookingStatus === 'CHECKED_IN' ? '#e8f5e9' : b.bookingStatus === 'NO_SHOW' ? '#fbe9e7' : '#e3f2fd',
                            color: b.bookingStatus === 'CHECKED_IN' ? '#2e7d32' : b.bookingStatus === 'NO_SHOW' ? '#bf360c' : '#1565c0',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={b.collectedAtStudio ? 'text.disabled' : undefined}>{formatINR(b.basePrice)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={b.collectedAtStudio ? 'text.disabled' : 'text.secondary'}>{formatINR(b.taxes)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color={b.collectedAtStudio ? 'text.disabled' : undefined} sx={b.collectedAtStudio ? { textDecoration: 'line-through' } : undefined}>{formatINR(b.total)}</Typography>
                      </TableCell>
                      <TableCell>
                        {b.collectedAtStudio ? (
                          <Tooltip title="Collected directly by the studio — not included in totals or settlement">
                            <Chip label="Paid at Studio" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, borderRadius: 1, bgcolor: '#fff8e1', color: '#b26a00' }} />
                          </Tooltip>
                        ) : (
                          <Chip label={b.modeOfPayment} size="small" sx={{ height: 18, fontSize: 10, borderRadius: 1, bgcolor: '#f5f5f5', color: 'text.secondary' }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : detailsData && detailsTab === 1 ? (
            /* ── Packs Tab ── */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa', fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Pack</TableCell>
                    <TableCell align="center">Classes</TableCell>
                    <TableCell>Purchased</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsData.packs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No pack purchases this week</Typography>
                      </TableCell>
                    </TableRow>
                  ) : detailsData.packs.map((p, i) => (
                    <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.disabled', fontSize: 12 }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>{p.user?.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140, display: 'block' }}>{p.user?.phone || p.user?.email || ''}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{p.packName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{p.totalClasses ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{p.purchasedAt ? formatDate(p.purchasedAt) : '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={p.collectedAtStudio ? 'text.disabled' : undefined}>{formatINR(p.basePrice)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={p.collectedAtStudio ? 'text.disabled' : 'text.secondary'}>{formatINR(p.taxes)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color={p.collectedAtStudio ? 'text.disabled' : undefined} sx={p.collectedAtStudio ? { textDecoration: 'line-through' } : undefined}>{formatINR(p.total)}</Typography>
                      </TableCell>
                      <TableCell>
                        {p.collectedAtStudio ? (
                          <Tooltip title="Collected directly by the studio — not included in totals or settlement">
                            <Chip label="Paid at Studio" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, borderRadius: 1, bgcolor: '#fff8e1', color: '#b26a00' }} />
                          </Tooltip>
                        ) : (
                          <Chip label={p.modeOfPayment} size="small" sx={{ height: 18, fontSize: 10, borderRadius: 1, bgcolor: '#f5f5f5', color: 'text.secondary' }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DialogContent>

        {detailsData && !detailsLoading && (
          <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 3, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {detailsTab === 0
                ? `${detailsData.bookings.length} bookings · Total: `
                : `${detailsData.packs.length} packs · Total: `}
              <strong>
                {formatINR(detailsTab === 0 ? detailsData.totals.bookingRevenue : detailsData.totals.packRevenue)}
              </strong>
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setDetailsStmt(null)} sx={{ textTransform: 'none', borderRadius: 2 }} size="small">
              Close
            </Button>
          </Box>
        )}
      </Dialog>
      </>
      )}

      {payoutsMounted && (
        <Box sx={{ display: pageTab === 1 ? 'block' : 'none' }}>
          <StudioPayoutsTab />
        </Box>
      )}
    </Box>
  );
};

export default StudioPaymentsPage;
